import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SECONDS = 60;

const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

const MicButton = ({ onText, testid = "mic", compact = false }) => {
  const { lang, t } = useLanguage();
  const v = t.ai.voice;
  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => { clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach((tr) => tr.stop()); }, []);
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return null;

  const transcribe = async (blob, mime) => {
    setState("transcribing");
    try {
      const fd = new FormData();
      fd.append("file", blob, `voice.${mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm"}`);
      fd.append("lang", lang);
      const r = await axios.post(`${API}/ai/transcribe`, fd);
      onText(r.data.text);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : v.error);
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        clearInterval(timerRef.current);
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 1000) { setState("idle"); toast.error(v.tooShort); return; }
        transcribe(blob, type);
      };
      recRef.current = rec;
      rec.start();
      setSeconds(0);
      setState("recording");
      timerRef.current = setInterval(() => setSeconds((s) => { if (s + 1 >= MAX_SECONDS) stop(); return s + 1; }), 1000);
    } catch {
      toast.error(v.denied);
    }
  };

  const stop = () => { if (recRef.current?.state === "recording") recRef.current.stop(); };

  const base = compact ? "h-10 w-10" : "px-4 py-3";
  if (state === "transcribing") {
    return <span data-testid={`${testid}-transcribing`} className={`inline-flex items-center justify-center gap-2 rounded-full bg-mint font-mono text-[11px] uppercase tracking-[0.15em] text-forest ${base}`}><Loader2 className="h-4 w-4 animate-spin" />{!compact && v.transcribing}</span>;
  }
  if (state === "recording") {
    return (
      <button type="button" onClick={stop} data-testid={`${testid}-stop`} aria-label={v.stop} className={`inline-flex items-center justify-center gap-2 rounded-full bg-red-500 font-mono text-[11px] uppercase tracking-[0.15em] text-white shadow-lg shadow-red-500/30 ${base}`}>
        <Square className="h-3.5 w-3.5 animate-pulse" />{!compact && `${v.stop} · ${seconds}s`}
      </button>
    );
  }
  return (
    <button type="button" onClick={start} data-testid={`${testid}-start`} aria-label={v.start} title={v.start} className={`inline-flex items-center justify-center gap-2 rounded-full border border-forest/30 bg-white font-mono text-[11px] uppercase tracking-[0.15em] text-forest transition-colors hover:border-forest hover:bg-forest hover:text-white ${base}`}>
      <Mic className="h-4 w-4" />{!compact && v.start}
    </button>
  );
};

export default MicButton;
